/**
 * Exportaciones — la segunda mitad del issue #44.
 *
 * Cinco tipos de exportación, todos agregados o inventario propio de la marca:
 * alertas, unidades, lotes, escaneos por día y celdas geográficas. **Ninguna
 * tiene una fila por consumidor**, y no es una decisión de esta pantalla: es
 * un `CHECK` en `export_job.kind` (ver `db/migrations/0006`). El cliente no
 * puede pedir otra cosa aunque se lo proponga, que es como tiene que estar la
 * regla 5 del dominio en el borde por donde los datos salen de la plataforma.
 *
 * Y queda registrado quién exportó: `export_job.requested_email` se
 * desnormaliza a propósito para poder responder "quién se llevó estos datos"
 * años después, y cada descarga escribe en `audit_log`. Esta vista **muestra**
 * ese correo en la lista en vez de esconderlo — un registro de auditoría que
 * nadie ve no disuade a nadie.
 *
 * El trabajo es asíncrono: se crea con 202 y se sigue por sondeo. Se sondea
 * sólo mientras haya algo en `queued` o `running`, no siempre: un dashboard
 * abierto todo el día no tiene por qué golpear la API cada cinco segundos para
 * mirar una lista que no cambia.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { useKeysetQuery } from '@/api/hooks';
import { exportKeys } from '@/api/keys';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import type { MessageKey } from '@/i18n/messages';
import type { Translate } from '@/i18n/translate';
import { useI18n } from '@/i18n/useI18n';
import { Badge, type Tone } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { AnalyticsTabs } from './AnalyticsTabs';
import { downloadExport, type ExportJob } from './download';

/** `export_job_kind_ck`. Cualquier otro valor lo rechaza la base. */
const KINDS = ['scans_daily', 'geo_cells', 'alerts', 'batches', 'units'] as const;
type Kind = (typeof KINDS)[number];

const KIND_KEYS: Record<string, MessageKey> = {
  scans_daily: 'analytics.export.kind.scansDaily',
  geo_cells: 'analytics.export.kind.geoCells',
  alerts: 'analytics.export.kind.alerts',
  batches: 'analytics.export.kind.batches',
  units: 'analytics.export.kind.units',
  // Se pide desde Catálogo › Etiquetas › Códigos con los filtros puestos
  // (#175), no desde acá: sin filtros sería la marca entera.
  labels: 'analytics.export.kind.labels',
};

/** `export_job_format_ck`. `csv_excel` lleva BOM y punto y coma. */
type Format = 'csv' | 'csv_excel';

/** `export_job_status_ck`. Un estado desconocido no debe dejar la celda vacía. */
const STATUS_KEYS: Record<string, MessageKey> = {
  queued: 'analytics.export.status.queued',
  running: 'analytics.export.status.running',
  done: 'analytics.export.status.done',
  failed: 'analytics.export.status.failed',
  expired: 'analytics.export.status.expired',
};

const STATUS_TONES: Record<string, Tone> = {
  queued: 'progress',
  running: 'progress',
  done: 'verified',
  failed: 'critical',
  expired: 'neutral',
};

const PAGE_SIZE = 25;
/** Sondeo mientras algo está en curso. Un CSV del tenant tarda segundos. */
const POLL_MS = 4_000;

interface FormState {
  kind: Kind;
  format: Format;
  /** `AAAA-MM-DD` del `<input type="date">`, o cadena vacía. */
  since: string;
  /** Id de lote como texto, para poder estar vacío. */
  batchId: string;
}

const EMPTY_FORM: FormState = { kind: 'scans_daily', format: 'csv', since: '', batchId: '' };

export function ExportsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [downloaded, setDownloaded] = useState<string | null>(null);

  const jobs = useKeysetQuery<ExportJob>(
    exportKeys.list(PAGE_SIZE),
    '/api/v1/exports',
    undefined,
    { limit: PAGE_SIZE },
  );
  const { query } = jobs;

  const pending = jobs.items.some(
    (job) => job.status === 'queued' || job.status === 'running',
  );

  const create = useMutation<ExportJob, unknown, FormState>({
    mutationFn: (input) =>
      api.post<ExportJob>('/api/v1/exports', {
        kind: input.kind,
        format: input.format,
        // La API espera un instante; el `<input type="date">` da una fecha.
        // Se ancla en UTC a medianoche, que es como el backend compara contra
        // `day` en los agregados.
        ...(input.since === '' ? {} : { since: `${input.since}T00:00:00Z` }),
        ...(input.batchId.trim() === '' ? {} : { batch_id: Number(input.batchId) }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exportKeys.all });
    },
  });

  const download = useMutation<string, unknown, ExportJob>({
    mutationFn: (job) => downloadExport(job, { api }),
    onSuccess: (name) => {
      setDownloaded(name);
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    create.mutate(form);
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      {pending && <ExportPoller />}
      <AnalyticsTabs />

      {can('analyst') && (
        <Card>
          <CardHeader
            title={t('analytics.export.new')}
            description={t('analytics.export.newDescription')}
          />
          <CardBody>
            <form onSubmit={submit} className="flex flex-wrap items-end gap-4">
              <Field label={t('analytics.export.kind')}>
                <select
                  className={SELECT_CLASS}
                  value={form.kind}
                  onChange={(event) => {
                    setForm({ ...form, kind: event.target.value as Kind });
                  }}
                >
                  {KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kindLabel(kind, t)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t('analytics.export.format')}>
                <select
                  className={SELECT_CLASS}
                  value={form.format}
                  onChange={(event) => {
                    setForm({ ...form, format: event.target.value as Format });
                  }}
                >
                  <option value="csv">{t('analytics.export.format.csv')}</option>
                  <option value="csv_excel">{t('analytics.export.format.excel')}</option>
                </select>
              </Field>

              <Field label={t('analytics.export.since')}>
                <input
                  type="date"
                  className={SELECT_CLASS}
                  value={form.since}
                  onChange={(event) => {
                    setForm({ ...form, since: event.target.value });
                  }}
                />
              </Field>

              <Field label={t('analytics.export.batch')}>
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  className={`${SELECT_CLASS} w-28`}
                  value={form.batchId}
                  onChange={(event) => {
                    setForm({ ...form, batchId: event.target.value });
                  }}
                />
              </Field>

              <Button type="submit" variant="primary" size="sm" disabled={create.isPending}>
                {create.isPending ? t('analytics.export.creating') : t('analytics.export.create')}
              </Button>
            </form>

            <p className="mt-3 text-xs text-ink-subtle">{t('analytics.export.privacy')}</p>

            {create.isError && (
              <div className="mt-3">
                <ErrorState error={create.error} />
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title={t('analytics.export.title')}
          description={t('analytics.export.description')}
          actions={
            pending ? <Badge tone="suspect">{t('analytics.export.working')}</Badge> : undefined
          }
        />

        {download.isError && (
          <CardBody>
            <ErrorState error={download.error} />
          </CardBody>
        )}
        {downloaded !== null && (
          <CardBody className="border-b border-border text-xs text-ink-muted">
            {t('analytics.export.downloaded', { name: downloaded })}
          </CardBody>
        )}

        {query.isPending && <LoadingState rows={4} />}

        {query.isError && (
          <CardBody>
            <ErrorState
              error={query.error}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </CardBody>
        )}

        {!query.isPending && !query.isError && jobs.items.length === 0 && (
          <EmptyState title={t('analytics.export.empty')} detail={t('analytics.export.emptyHint')} />
        )}

        {jobs.items.length > 0 && (
          <>
            <Table caption={t('analytics.export.description')}>
              <thead>
                <tr>
                  <Th>{t('analytics.export.column.kind')}</Th>
                  <Th>{t('analytics.export.column.status')}</Th>
                  <Th className="text-right">{t('analytics.export.column.rows')}</Th>
                  <Th>{t('analytics.export.column.requestedBy')}</Th>
                  <Th>{t('analytics.export.column.created')}</Th>
                  <Th>{t('analytics.export.column.expires')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {jobs.items.map((job) => (
                  <tr key={job.id}>
                    <Td className="font-medium">
                      {kindLabel(job.kind, t)}
                      <span className="ml-2 text-xs text-ink-subtle">
                        {job.format === 'csv_excel'
                          ? t('analytics.export.format.excel')
                          : t('analytics.export.format.csv')}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONES[job.status] ?? 'neutral'}>
                        {statusLabel(job.status, t)}
                      </Badge>
                      {job.error !== null && job.error !== undefined && (
                        <p className="mt-1 max-w-xs text-xs text-critical">{job.error}</p>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums text-ink-muted">
                      {job.row_count === null || job.row_count === undefined
                        ? '—'
                        : fmt.number(job.row_count)}
                    </Td>
                    <Td className="text-ink-muted">{job.requested_email ?? '—'}</Td>
                    <Td className="text-ink-muted">{fmt.dateTime(job.created_at)}</Td>
                    <Td className="text-ink-muted">
                      {job.expires_at === null || job.expires_at === undefined
                        ? '—'
                        : fmt.relative(job.expires_at)}
                    </Td>
                    <Td className="text-right">
                      {/* `can('analyst')` como el formulario de arriba: desde
                          #88 la descarga exige analyst, igual que la creación.
                          Un viewer ve la lista —saber qué se exportó y cuándo
                          es auditoría, no datos— pero no el botón que termina
                          en 403. */}
                      {job.status === 'done' && can('analyst') && (
                        <Button
                          size="sm"
                          disabled={download.isPending}
                          onClick={() => {
                            download.mutate(job);
                          }}
                        >
                          {t('analytics.export.download')}
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {jobs.hasMore ? (
                <Button size="sm" onClick={jobs.loadMore} disabled={jobs.isLoadingMore}>
                  {jobs.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Enciende el sondeo mientras haya un trabajo en curso, y lo apaga solo.
 *
 * Es un componente y no un `useState` + `useEffect` en la página porque el
 * sondeo es una condición del render, no un estado que haya que sincronizar:
 * si hay algo en `queued` o `running`, este componente está montado y agrega
 * un observador con `refetchInterval` sobre **la misma** entrada de cache; si
 * no, se desmonta y el sondeo se apaga. Ni cascada de renders ni un intervalo
 * que sobreviva a la condición que lo justificaba.
 */
function ExportPoller(): null {
  useKeysetQuery<ExportJob>(exportKeys.list(PAGE_SIZE), '/api/v1/exports', undefined, {
    limit: PAGE_SIZE,
    refetchInterval: POLL_MS,
  });
  return null;
}

const SELECT_CLASS =
  'h-8 rounded border border-border-strong bg-surface px-2 text-sm text-ink';

/**
 * Un `kind` o un `status` que la API agregue después y este catálogo no tenga
 * se muestra crudo. Es feo a propósito: una celda vacía se lee como un bug del
 * dato, y una cadena en inglés en medio de la tabla se lee como lo que es —una
 * traducción que falta.
 */
function kindLabel(kind: string, t: Translate): string {
  const key = KIND_KEYS[kind];
  return key === undefined ? kind : t(key);
}

function statusLabel(status: string, t: Translate): string {
  const key = STATUS_KEYS[status];
  return key === undefined ? status : t(key);
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
      {label}
      {children}
    </label>
  );
}
